/**
 * React Query Deduplication Optimizations
 * Advanced query deduplication to reduce network requests
 */

import { Query, QueryCache, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

export interface DeduplicationOptions {
  // Enable deduplication
  enabled?: boolean;

  // Deduplication window in milliseconds
  window?: number;

  // Maximum number of duplicate queries to track
  maxSize?: number;

  // Deduplication strategies
  strategies?: {
    // Exact key match
    exact?: boolean;

    // Similar key match (e.g., same resource, different params)
    similar?: boolean;

    // Partial key match (e.g., parent resource)
    partial?: boolean;
  };

  // Custom deduplication key generator
  getDeduplicationKey?: (queryKey: QueryKey) => string;

  // Callback when deduplication occurs
  onDeduplicate?: (original: QueryKey, duplicate: QueryKey) => void;
}

export interface QueryDeduplicationInfo {
  key: string;
  queryKey: QueryKey;
  timestamp: number;
  promise: Promise<any>;
  subscribers: number;
}

const DEFAULT_OPTIONS: Required<DeduplicationOptions> = {
  enabled: true,
  window: 100, // 100ms window
  maxSize: 1000,
  strategies: {
    exact: true,
    similar: false,
    partial: false,
  },
  getDeduplicationKey: (queryKey) => JSON.stringify(queryKey),
  onDeduplicate: () => {},
};

/**
 * Advanced query deduplication manager
 */
export class QueryDeduplicationManager {
  private options: Required<DeduplicationOptions>;
  private pendingQueries: Map<string, QueryDeduplicationInfo> = new Map();
  private queryClient: QueryClient;
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(queryClient: QueryClient, options: DeduplicationOptions = {}) {
    this.queryClient = queryClient;
    this.options = { ...DEFAULT_OPTIONS, ...options };

    if (this.options.enabled) {
      this.startCleanupTimer();
    }
  }

  /**
   * Execute query with deduplication
   */
  async execute<T>(
    queryKey: QueryKey,
    queryFn: () => Promise<T>,
    options?: { deduplicationKey?: string }
  ): Promise<T> {
    if (!this.options.enabled) {
      return queryFn();
    }

    const deduplicationKey = options?.deduplicationKey || this.getDeduplicationKey(queryKey);
    const existing = this.findExistingQuery(deduplicationKey, queryKey);

    if (existing) {
      // Found duplicate query
      existing.subscribers++;
      this.options.onDeduplicate(existing.queryKey, queryKey);

      logger.info('📋 Query deduplicated', {
        eventType: 'QUERY_DEDUPLICATED',
        deduplicationKey,
        originalKey: existing.queryKey,
        duplicateKey: queryKey,
        subscribers: existing.subscribers,
      });

      return existing.promise as Promise<T>;
    }

    // Create new query
    const timestamp = Date.now();
    const promise = queryFn();

    const queryInfo: QueryDeduplicationInfo = {
      key: deduplicationKey,
      queryKey,
      timestamp,
      promise,
      subscribers: 1,
    };

    this.pendingQueries.set(deduplicationKey, queryInfo);

    // Clean up after promise resolves
    promise.finally(() => {
      const info = this.pendingQueries.get(deduplicationKey);
      if (info && info.subscribers <= 1) {
        this.pendingQueries.delete(deduplicationKey);
      } else if (info) {
        info.subscribers--;
      }
    });

    return promise;
  }

  /**
   * Find existing query that matches the deduplication key
   */
  private findExistingQuery(
    deduplicationKey: string,
    queryKey: QueryKey
  ): QueryDeduplicationInfo | null {
    const now = Date.now();

    // Check for exact match first
    const exact = this.pendingQueries.get(deduplicationKey);
    if (exact && now - exact.timestamp < this.options.window) {
      return exact;
    }

    // Check for similar queries if enabled
    if (this.options.strategies.similar) {
      const similar = this.findSimilarQuery(queryKey);
      if (similar) return similar;
    }

    // Check for partial matches if enabled
    if (this.options.strategies.partial) {
      const partial = this.findPartialQuery(queryKey);
      if (partial) return partial;
    }

    return null;
  }

  /**
   * Find similar query (e.g., same resource, different params)
   */
  private findSimilarQuery(queryKey: QueryKey): QueryDeduplicationInfo | null {
    const now = Date.now();

    for (const [_, info] of this.pendingQueries) {
      if (now - info.timestamp > this.options.window) continue;

      // Check if queries are similar (same first part of key)
      const currentKey = Array.isArray(queryKey) ? queryKey : [queryKey];
      const infoKey = Array.isArray(info.queryKey) ? info.queryKey : [info.queryKey];

      if (currentKey[0] === infoKey[0] && currentKey.length === infoKey.length) {
        // Check if only the last part differs (pagination, filters, etc.)
        const isSimilar = currentKey.slice(0, -1).every((part, index) =>
          part === infoKey[index]
        );

        if (isSimilar) {
          return info;
        }
      }
    }

    return null;
  }

  /**
   * Find partial query match (e.g., parent resource)
   */
  private findPartialQuery(queryKey: QueryKey): QueryDeduplicationInfo | null {
    const now = Date.now();

    for (const [_, info] of this.pendingQueries) {
      if (now - info.timestamp > this.options.window) continue;

      // Check if current query is a subset of pending query
      const currentKey = Array.isArray(queryKey) ? queryKey : [queryKey];
      const infoKey = Array.isArray(info.queryKey) ? info.queryKey : [info.queryKey];

      if (infoKey.length > currentKey.length) {
        const isSubset = currentKey.every((part, index) =>
          part === infoKey[index]
        );

        if (isSubset) {
          return info;
        }
      }
    }

    return null;
  }

  /**
   * Generate deduplication key for query
   */
  private getDeduplicationKey(queryKey: QueryKey): string {
    if (this.options.getDeduplicationKey) {
      return this.options.getDeduplicationKey(queryKey);
    }

    // Default: JSON stringify with sorting for consistency
    const normalized = this.normalizeQueryKey(queryKey);
    return JSON.stringify(normalized);
  }

  /**
   * Normalize query key for consistent deduplication
   */
  private normalizeQueryKey(queryKey: QueryKey): QueryKey {
    if (!Array.isArray(queryKey)) {
      return [queryKey];
    }

    // Remove undefined/null values and sort objects
    return queryKey.map((key) => {
      if (typeof key === 'object' && key !== null) {
        return this.sortObject(key);
      }
      return key;
    });
  }

  /**
   * Deep sort object keys for consistent comparison
   */
  private sortObject(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(item =>
        typeof item === 'object' && item !== null
          ? this.sortObject(item)
          : item
      );
    }

    if (typeof obj === 'object' && obj !== null) {
      const sorted: any = {};
      const keys = Object.keys(obj).sort();

      for (const key of keys) {
        const value = obj[key];
        sorted[key] =
          typeof value === 'object' && value !== null
            ? this.sortObject(value)
            : value;
      }

      return sorted;
    }

    return obj;
  }

  /**
   * Start cleanup timer to remove old entries
   */
  private startCleanupTimer(): void {
    // Clean up every 30 seconds
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 30000);
  }

  /**
   * Clean up old entries
   */
  private cleanup(): void {
    const now = Date.now();
    let removed = 0;

    for (const [key, info] of this.pendingQueries) {
      // Remove queries older than the window
      if (now - info.timestamp > this.options.window * 10) {
        this.pendingQueries.delete(key);
        removed++;
      }
    }

    // Enforce max size
    if (this.pendingQueries.size > this.options.maxSize) {
      const entries = Array.from(this.pendingQueries.entries());
      const toRemove = entries.length - this.options.maxSize;

      // Remove oldest entries
      entries
        .sort(([, a], [, b]) => a.timestamp - b.timestamp)
        .slice(0, toRemove)
        .forEach(([key]) => {
          this.pendingQueries.delete(key);
          removed++;
        });
    }

    if (removed > 0) {
      logger.info('🧹 Cleaned up deduplication cache', {
        eventType: 'DEDUP_CLEANUP',
        removed,
        remaining: this.pendingQueries.size,
      });
    }
  }

  /**
   * Get deduplication statistics
   */
  getStats(): {
    pendingQueries: number;
    window: number;
    maxSize: number;
    strategies: DeduplicationOptions['strategies'];
  } {
    return {
      pendingQueries: this.pendingQueries.size,
      window: this.options.window,
      maxSize: this.options.maxSize,
      strategies: this.options.strategies,
    };
  }

  /**
   * Clear all pending queries
   */
  clear(): void {
    this.pendingQueries.clear();
    logger.info('🗑️ Cleared deduplication cache', {
      eventType: 'DEDUP_CLEARED',
    });
  }

  /**
   * Destroy the manager and clean up
   */
  destroy(): void {
    this.clear();
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

/**
 * Create query deduplication manager
 */
export function createQueryDeduplication(
  queryClient: QueryClient,
  options?: DeduplicationOptions
): QueryDeduplicationManager {
  return new QueryDeduplicationManager(queryClient, options);
}

/**
 * Hook to use query deduplication
 */
export function useQueryDeduplication(
  queryClient: QueryClient,
  options?: DeduplicationOptions
) {
  const managerRef = useRef<QueryDeduplicationManager | null>(null);

  useEffect(() => {
    if (!managerRef.current) {
      managerRef.current = createQueryDeduplication(queryClient, options);
    }

    return () => {
      managerRef.current?.destroy();
    };
  }, [queryClient, options]);

  return managerRef.current;
}

/**
 * Common deduplication key generators
 */
export const DeduplicationKeyGenerators = {
  // Ignore pagination parameters
  ignorePagination: (queryKey: QueryKey): string => {
    const normalized = Array.isArray(queryKey) ? [...queryKey] : [queryKey];

    // Remove common pagination parameters
    const last = normalized[normalized.length - 1];
    if (typeof last === 'object' && last !== null) {
      const { page, limit, offset, ...rest } = last as any;
      normalized[normalized.length - 1] = rest;
    }

    return JSON.stringify(normalized);
  },

  // Ignore sorting parameters
  ignoreSorting: (queryKey: QueryKey): string => {
    const normalized = Array.isArray(queryKey) ? [...queryKey] : [queryKey];

    // Remove sorting parameters
    const last = normalized[normalized.length - 1];
    if (typeof last === 'object' && last !== null) {
      const { sortBy, sortOrder, ...rest } = last as any;
      normalized[normalized.length - 1] = rest;
    }

    return JSON.stringify(normalized);
  },

  // Ignore filter parameters (keep only resource identifier)
  resourceOnly: (queryKey: QueryKey): string => {
    const normalized = Array.isArray(queryKey) ? [...queryKey] : [queryKey];

    // Keep only the first part (resource name)
    return JSON.stringify([normalized[0]]);
  },
};

// Import React hooks
import { useRef, useEffect } from "react";